import React, {useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import SignInBaseLayout from "../../common/SignInBaseLayout";
import EnterNewPasswordForm from "./EnterNewPasswordForm";
import {
  EnterNewPasswordStep,
  NewPasswordDataTo,
  NewPasswordTo, PasswordSuccessHandler,
  PasswortResetAnswer,
  TotpToken
} from "./types";
import EnterNewPasswordMFAForm from "./EnterNewPasswordMFAForm";
import axios, {AxiosResponse} from "axios";
import MfaResetForm from "../authenticate/mfa/resetMfa/MfaResetForm";
import {AuthenticationHandler, redirect, redirectVariant} from "../authenticate/useAuthenticate";
import {LoginCredentials} from "../authenticate/types";
import {GenericVars} from "../../common/types";
import {useLxTranslations} from "@lxo-blocks/react";
import {FORGOT_PASSWORD_FORM_TRANSLATION} from "./ForgotPasswordForm.translation.de";

export default function EnterNewPasswordView(props: any) {

  const [currentStep, setCurrentStep] = useState<EnterNewPasswordStep>(EnterNewPasswordStep.ENTER_NEW_PASSWORD);
  const [currentPasswordTo, setCurrentPasswordTo] = useState<NewPasswordDataTo>();
  const [userId, setUserId] = useState<string>("");
  const [currentError, setCurrentError] = useState<string>("");
  const {confirmationTicketId} = useParams<{ confirmationTicketId: string }>();
  const {t} = useLxTranslations(FORGOT_PASSWORD_FORM_TRANSLATION);
  const navigate = useNavigate();

  const successHandler: PasswordSuccessHandler = {
    onSuccess: (response: PasswortResetAnswer) => {
      handleRedirectToLogin(response);
    },
    onMfa: () => {
      setCurrentStep(EnterNewPasswordStep.MFA);
    },
    onMfaReset: () => {
      setCurrentStep(EnterNewPasswordStep.RESET_MFA);
    },
    onError: (error: any) => {
      handleError(error);
    },
  }

  const authenticationHandler: AuthenticationHandler = {
    onSuccess: (entryPoint, customRedirect) => {//NOSONAR
      redirect(entryPoint, customRedirect);
    },
    onVariantLoginSuccess(entryPoint, customRedirect, variant): void { //NOSONAR
      redirectVariant(entryPoint, customRedirect, variant);
    },
    onMfaRequired: (currentLoginCredentials: LoginCredentials, userId: string) => {//NOSONAR
    },
    onResetMfa: () => {//NOSONAR
    },
    onChangeMethod: () => {//NOSONAR
    },
  }

  const handleRedirectToLogin = (response: PasswortResetAnswer) => {
    let redirectUrl = "/authenticate";
    if (response.email !== "") {
      sessionStorage.setItem(GenericVars.SESSION_STORAGE_USERNAME, response.email);
      redirectUrl = redirectUrl + "?message=SET_NEW_PASSWORD_SUCCESSFULLY";
    }
    navigate(redirectUrl);
  }

  const handleError = (error: any) => {
    switch (error.response.status) {
      case 423: // MFA Required
        setUserId(error.response.data);
        setCurrentStep(EnterNewPasswordStep.MFA)
        break;
      case 424:
        //Wrong MFa Code
        setUserId(error.response.data);
        setCurrentError(t("TOTP_CODE_WRONG"));
        break;
      default: {
        if (error.response?.data?.IssueList[0]?.i18nKey === "invalid_confirmation_ticket") {
          setCurrentError(t("CONFIRMATION_TICKET_NOT_VALID"));
        } else {
          setCurrentError(t("GENERAL_ERROR_TEXT"));
        }
      }
    }
  }

  function getNormalizedTotpCode(inputCode: string | undefined): string {
    return inputCode ? inputCode.replace(/[^0-9]/gi, "").trim() : "";
  }

  async function submitPasswordWithMFA(totpToken: TotpToken) {
    let updatedPasswordDataTo = {
      confirmationId: currentPasswordTo?.confirmationId,
      password1: currentPasswordTo?.password1,
      password2: currentPasswordTo?.password2,
      verificationCode: getNormalizedTotpCode(totpToken.totptoken)
    }
    return submitData(updatedPasswordDataTo);
  }

  async function submitPassword(newPassword: NewPasswordTo) {
    let newPasswordDataTo = {
      confirmationId: confirmationTicketId,
      password1: newPassword.password1,
      password2: newPassword.password2,
    }

    setCurrentPasswordTo(newPasswordDataTo);
    return submitData(newPasswordDataTo);
  }

  async function submitData(updatedPasswordDataTo: NewPasswordDataTo) {
    setCurrentError("");
    try {
      let resetResult: AxiosResponse<PasswortResetAnswer> = await axios.post("/grld-public/forgotpassword/v100/reset", updatedPasswordDataTo)
      successHandler.onSuccess(resetResult.data);
    } catch (error) {
      successHandler.onError(error);
    }
  }

  return (
    <SignInBaseLayout headline_i18n={currentStep.valueOf()} subtitle_i18n={currentStep.valueOf() + "_subtitle"}>
      {currentStep === EnterNewPasswordStep.ENTER_NEW_PASSWORD && (
        <EnterNewPasswordForm successHandler={successHandler} confirmationTicketId={confirmationTicketId}
                              currentError={currentError} submitMethod={submitPassword}/>)}
      {currentStep === EnterNewPasswordStep.MFA && (
        <EnterNewPasswordMFAForm currentError={currentError} successHandler={successHandler}
                                 submitMethod={submitPasswordWithMFA}/>)}
      {currentStep === EnterNewPasswordStep.RESET_MFA && (
        <MfaResetForm authenticationHandler={authenticationHandler} userId={userId}/>)}
    </SignInBaseLayout>
  );
}
